import { Component } from '@angular/core';
import { OnInit, Input, ElementRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../services/alerts.service';
import { ProductService } from '../../services/product.service';
import { UserService } from '../../services/user.service';
import { SearchService } from '../../search/services/search.service';
import { GeoTagComponent } from '../../map/geo-tag/geo-tag.component';

import cloneDeep from 'lodash/cloneDeep';
import flatpickr from 'flatpickr';

/**
 * Component providing an edit view of the metadata of a group of products.
 */
@Component({
	selector: 'global-edit',
	templateUrl: './global-edit.component.html',
	styleUrl: './global-edit.component.css'
})
export class GlobalEditComponent implements OnInit {
	@Input() productGroup: any; // productGroup.records = all records in the current result page, productGroup.ids = selected prods

	LOCALIZATION: any;
	Object = Object;

	mode: string = 'update';        // update or replace
	replaceMode: string = 'replace' // replace or append

	// The result page records, filtered to include only the selected products.
	batchRecords: any[] = [];

	// The set of all fields from each product's corresponding library
	// schema and form template.
	masterEditFieldList: any[] = [];

	// The set of fields available for the user to edit. 
	// This includes the user's available / metadata fields, minus any special system fields.
	fieldList: any[] = [];

	// The set of fields the user has chosen to edit.
	batchEditFields: any[] = [];

	// The set of fields the user has chosen to search/replace.
	batchSearchReplaceFields: any[] = [];

	selectedRecordId: number | undefined = undefined;
	selectedRecord: any = {};

	dateFormat: string = 'MM/dd/yyyy';
	datePickerOptions: any = {};
	datePickerSet: any = {};
	inprogress: boolean = false;

	constructor(
		private userService: UserService,
		private alertsService: AlertsService,
		private productService: ProductService,
		private searchService: SearchService,
		private elementRef: ElementRef,
		private ngbmodalService: NgbActiveModal,
		private ngbModal: NgbModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.dateFormat = this.userService.getDateFormat();
		this.initializeForm();
	}

	private async initializeForm(): Promise<any> {
		this.filterSelectedRecords();

		// The libraries of the selected products.
		// If more than one library is 'selected' we must compare the edit schemas of all of them for conflicts.
		let selectedLibs = this.getSelectedLibraries();

		const forms = await this.getMetadataFormTemplates(selectedLibs);
		const schemas = await this.getMetadataSchemas(selectedLibs);

		if (forms.length == 0 || !schemas || schemas.length == 0) {
			this.alertsService.error({
				message: "Failed to get the edit form for the selected products."
			});
		}
		else {
			this.buildGlobalForm(forms, schemas);
			this.loadFields();
			this.loadMetadataDisplay();
		}
	}

	private filterSelectedRecords(): void {
		for (let i = 0; i < this.productGroup.records.length; i++) {
			if (this.productGroup.ids.includes(this.productGroup.records[i].id)) {
				this.batchRecords.push(this.productGroup.records[i]);
			}
		}
	}

	/**
	 * Determines the distinct set of libraries of origin of all products in the selected group.
	 */
	private getSelectedLibraries(): string[] {
		let selectedLibs: string[] = [];
		for (let i = 0; i < this.batchRecords.length; i++) {
			let lib = this.batchRecords[i].aw_library_id;
			if (!selectedLibs.includes(lib)) {
				selectedLibs.push(lib);
			}
		}
		return selectedLibs;
	}

	private async getMetadataFormTemplates(libraryNames: string[]): Promise<any> {
		try {
			return await this.productService.getMetadataForms(libraryNames);
		} catch (err) {
			console.error("Failed to get metadata edit templates for the product libraries.", err);
			return undefined;
		}
	}

	private async getMetadataSchemas(libraryNames: string[]): Promise<any | undefined> {
		try {
			return await this.productService.getMetadataSchemas(libraryNames);
		} catch (err) {
			console.error("Failed to get metadata schemas for the product libraries.", err);
			return undefined;
		}
	}

	/** 
	 * Reconciles the templates and schemas into a master set of field properties.
	 */
	private buildGlobalForm(forms: any, schemas: any): void {
		for (let i = 0; i < forms.length; i++) {
			for (let k = 0; k < forms[i].fields.length; k++) {
				let field = forms[i].fields[k];
				let showWhen = field.show;
				field.show = (showWhen === "edit" || showWhen === "always") ? true : false;

				let masterField = this.masterEditFieldList.find((x: any) => { return x.name === field.name });
				// TODO: whether or not a field accepts multiple values should be exposed here in the metadata edit form template
				if (masterField) {
					// Reconcile differences:
					// placeholder - Show nothing in placeholder
					// readonly - Disable field, show message
					// required - Disable field, show message
					// min/max - Disable field, show message
					// defaultField - Show nothing in defaultfield
					// show - Disable field, show message
					if (masterField.placeholder !== field.placeholder) masterField.placeholder = "";
					if (masterField.defaultField !== field.defaultField) masterField.defaultField = "";
					if (masterField.readOnly !== field.readOnly ||
						masterField.required !== field.required ||
						masterField.min !== field.min ||
						masterField.max !== field.max ||
						masterField.show !== field.show) {
						masterField.disabled = true;
						masterField.message = this.LOCALIZATION.globalEditFieldDisabledNoMerge;
					}
				} else {
					// If the field is not present in any other schema, it will be used as-is
					this.masterEditFieldList.push(field);
				}
			}
		}

		// TODO If we want to support dependent fields in global edit, get the values here.
		// This currently checks the library data schema only to determine if the field accepts multiple values, as this
		// property is not available in the metadata edit form schema.
		for (let i = 0; i < schemas.length; i++) {
			for (const [key, field] of Object.entries(schemas[i].properties) as [string, any][]) {
				let masterField = this.masterEditFieldList.find((x: any) => x.name === key);
				if (masterField) {
					if (field.items) masterField.ismultiple = true;
				}
			}
		}
	}

	private loadFields(): void {
		// Seed the user's field list from their set of available fields,
		// filtering out fields that should remain read-only in this form
		this.fieldList = this.userService.user.properties.awfieldObjectList.filter((e: any) => {
			return e.isAvailable
				&& !e.name.startsWith('browse')
				&& !e.name.startsWith('ner_')
				&& e.name != "lb_document_id"
				&& e.name != "uuid"
				&& e.name != "topterms"
				&& e.name != "thumbnail"
				&& e.name != "product_views"
				&& e.name != "product_size"
				&& e.name != "fme_metadata"
				&& e.name != "document_category_mime"
				&& e.name != "field61_num"
				&& e.name != "field62_num"
				&& e.name != "field63_num"
				&& e.name != "field64_num"
				&& e.name != "archive_image"
				&& e.name != "library"
				&& e.name != "identifier"
				&& e.name != "file_name"
				&& e.name != "load_num"
				&& e.name != "modified_date"
				&& e.name != "load_date"
				&& e.name != "album_id";
		});

		for (let i = 0; i < this.fieldList.length; i++) {
			let formField = this.fieldList[i];
			let searchSessionField = this.userService.user.searchInputs?.find((x: any) => { return x.name === formField.name });

			if (searchSessionField) {
				formField.type = searchSessionField.type;
				formField.type_normalized = searchSessionField.type_normalized;
			} else {
				formField.type_normalized = formField.type.toUpperCase();
			}

			if ("INTEGER" === formField.type_normalized || "FLOAT" === formField.type_normalized ||
				"DECIMAL" === formField.type_normalized || "LONG" === formField.type_normalized) {
				formField.type = "NUMBER";
			} else if ("LIST" === formField.type_normalized || "LIST_MULTI" === formField.type_normalized || "RADIO" === formField.type_normalized) {
				formField.type = "LIST";
			}

			if (formField.type === "LIST") {
				let fieldCollection = this.userService.user.properties.awfieldCollectionObjectList.find((x: any) => { return x.field === formField.name });
				if (fieldCollection && fieldCollection.element) {
					formField.allowedValues = fieldCollection.element;
				} else {
					formField.allowedValues = [];
				}
			} else {
				formField.allowedValues = searchSessionField?.filteredOptions || [];
			}


			// Add any attributes from the combined edit form configs
			let schemaField = this.masterEditFieldList.find((x: any) => { return x.name === formField.name || x.form_name === formField.name });
			if (!schemaField) {
				formField.disabled = true;
				formField.message = this.LOCALIZATION.globalEditFieldDisabledNotFound;
			} else if (!schemaField.show) {
				formField.disabled = true;
				formField.message = this.LOCALIZATION.globalEditFieldDisabled;
			} else {
				formField.placeholder = schemaField.placeholder;
				formField.required = schemaField.required;
				formField.readOnly = schemaField.readOnly;
				formField.min = schemaField.min;
				formField.max = schemaField.max;
				formField.defaultValue = schemaField.defaultField;
				formField.disabled = schemaField.disabled;
				formField.message = "";
				formField.ismultiple = schemaField.ismultiple;
			}

			if (formField.disabled) {
				formField.type = "READONLY";
				if (schemaField && schemaField.message) {
					formField.message = schemaField.message;
				}
			}
			else if (formField.readOnly) {
				formField.type = "READONLY";
				formField.message = this.LOCALIZATION.globalEditFieldReadOnly;
			}
			else if (formField.type == "DATE") {
				// create field-specific this.datePickerOptions with min/max date
				let opts = this.userService.getDatePickerOptions(formField.min, formField.max, "down", formField.name);
				this.datePickerOptions[formField.name] = opts;
			}
		}

		// Populate the form with a starter field
		this.addBatchEditField();
		this.addBatchSearchReplaceField();
	}

	/**
	 * Adds a field to the form displayed as a select list of possible metadata field values to edit.
	 */
	addBatchEditField(): void {
		this.batchEditFields.push({
			name: this.fieldList[0].name,
			content: [""]
		});
		this.updateBatchEditField(this.batchEditFields.length - 1);
	}

	/**
	 * Updates the batch edit field based on the fieldIndex.
	 */
	updateBatchEditField(fieldIndex: number): void {
		let selectedField = this.fieldList.find((x: any) => {
			return x.name == this.batchEditFields[fieldIndex].name;
		});

		let newField = cloneDeep(selectedField);
		this.batchEditFields[fieldIndex] = newField;

		if (this.batchEditFields[fieldIndex].defaultValue) {
			this.batchEditFields[fieldIndex].content = [this.batchEditFields[fieldIndex].defaultValue];
		} else {
			this.batchEditFields[fieldIndex].content = [""];
		}
		
		
		let parentField = this.getParentFieldFromFieldList(fieldIndex);
		if (parentField) {
			// TODO get the parent field idx
			let parentFieldIndex = 0;
			for (parentFieldIndex; parentFieldIndex < this.batchEditFields.length; parentFieldIndex++) {
				if (this.batchEditFields[parentFieldIndex].name === parentField.name) {
					this.updateBatchEditFieldValue(parentFieldIndex, 0);
					break;
				}
			}
		}
		
		// If parent not in a list do not show options for children
		for (let i = 0; i < this.batchEditFields.length; i++) {
			let a = this.fieldList.find((x: any) => {
				return x.name == this.batchEditFields[i].name;
			});
			if (a.allowedValues &&
				(a.allowedValues[0] != null &&
					a.allowedValues[0].parentMask &&
					a.allowedValues[0].parentMask != '' ||
					a.allowedValues.length > 1 &&
					a.allowedValues[1] != null &&
					a.allowedValues[1].parentMask &&
					a.allowedValues[1].parentMask != ''
				)) {
				let parentField = this.getParentField(i);
				if (parentField == null) {
					this.batchEditFields[i].allowedValues = [];
				}
			}
		}
	}

	private loadMetadataDisplay(): void {
		let validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => { return x.isAvailable && x.isMetadata });

		// TODO this.batchRecords does not include the full metadata, only the initial
		// search result fields returned by CSW.  Calling searchService.getRecord() for each
		// selected product should only be done lazily.
		for (let i = 0; i < this.batchRecords.length; i++) {
			let record = this.batchRecords[i];
			record.metadataDisplay = [];
			let titleIndex = -1;
			for (let [key, value] of Object.entries(record)) {
				let userField = validFields.filter((x: any) => { return x.name === key });
				if (userField && userField.length > 0) {
					let datum = { field: key, label: userField[0].displayName, value: value };
					record.metadataDisplay.push(datum);
					if (key == 'title') titleIndex = record.metadataDisplay.length - 1;
				}
			}

			// Move 'title' to the front of the metadata array so it displays first
			if (titleIndex != -1) {
				record.metadataDisplay.unshift(record.metadataDisplay.splice(titleIndex, 1)[0]);
			}
		}
	}

	loadSelectedRecordMetadata(): void {
		this.selectedRecord = null;

		if (!this.selectedRecordId) {
			return;
		}

		this.productService.getMetadata(this.selectedRecordId).then(
			(result: any) => {
				let productInfo: any = {};
				if (result.Product && result.Product.aw_field) {
					let fields = result.Product.aw_field;
					for (let i = 0; i < fields.length; i++) {
						let field = fields[i].name;
						if (productInfo[field]) {
							productInfo[field].push(fields[i].content);
						} else {
							productInfo[field] = [fields[i].content];
						}
					}
					this.selectedRecord = productInfo;
				}
			},
			(err: any) => {
				console.error("Failed to get product metadata.", err);
				this.selectedRecord = null;
			}
		);
	}

	/**
	 * Copies values from the metadata of the currently selected Record to the edit fields added to the form.
	 */
	copyValues(): void {
		for (let i = 0; i < this.batchEditFields.length; i++) {
			let f = this.batchEditFields[i];
			let vals = this.selectedRecord && this.selectedRecord[f.name] ? [...this.selectedRecord[f.name]] : [""];
			for (let k = 0; k < f.content.length; k++) {
				f.content[k] = vals.shift();
			}
			if (vals.length > 0) {
				f.content.push(...vals);
			}
		}
	}

	dupeBatchEditField(fieldIndex: number): void {
		let dupeme = this.batchEditFields[fieldIndex];
		if (dupeme) {
			dupeme.content.push("");
		}
	}

	removeBatchEditField(fieldIndex: number, valueIndex: number): void {
		let field = this.batchEditFields[fieldIndex];
		if (field) {
			field.content.splice(valueIndex, 1);
			if (field.content.length == 0) {
				// Update children
				for (let i = 0; i < this.batchEditFields[fieldIndex].allowedValues.length; i++) {
					if (this.batchEditFields[fieldIndex].allowedValues[i] && this.batchEditFields[fieldIndex].allowedValues[i].childs) {
						for (let x = 0; x < this.batchEditFields[fieldIndex].allowedValues[i].childs.length; x++) {
							this.updateChildBatchEditField(this.batchEditFields[fieldIndex].allowedValues[i].childs[x].name, '');
						}
					}
				}
				this.batchEditFields.splice(fieldIndex, 1);
			}
		}
	}

	updateBatchEditFieldValue(fieldIndex: number, valueIndex: number): void {
		let contentValue = this.batchEditFields[fieldIndex].content[valueIndex] ? this.batchEditFields[fieldIndex].content[valueIndex] : null;
		let selectedFieldValue = this.batchEditFields[fieldIndex].allowedValues.filter((allowedValue: any) => (allowedValue != null && allowedValue.value == contentValue));
		if (selectedFieldValue && selectedFieldValue.length > 0 && selectedFieldValue[0].childs) {
			for (let x = 0; x < selectedFieldValue[0].childs.length; x++) {
				let childs = this.batchEditFields.filter((field: any) => {
					return field.name === selectedFieldValue[0].childs[x].name;
				});
				let availableChildField = this.fieldList.find((field: any) => {
					return field.name === selectedFieldValue[0].childs[x].name;
				});
				childs.forEach((child: any) => {
					let newOptions = availableChildField.allowedValues.filter((option: any) =>
						this.filterComparator(option.parentMask, contentValue)
					);
					child.allowedValues = newOptions;
				});
			}
		}
	}
	
	/**
	 * Custom filter function to compare values.
	 */
	private filterComparator(actual: string, expected: string): boolean {
		if (!actual || !expected) return false;

		// Example: "parentMask" : "en OR eng" 
		let parts = actual.split(" OR ");
		return parts.some((part: string) => part.toLowerCase() === expected.toLowerCase());
	}	

	updateChildBatchEditField(name: string, parentMask: any): void {
		let selectedFieldValueIndex = this.batchEditFields.findIndex((y: any) => {
			return y.name == name;
		});
		let childs = this.batchEditFields.filter((y: any) => {
			return y.name == name;
		});
		for (let i = 0; i < childs.length; i++) {
			let a = this.fieldList.find((x: any) => {
				return x.name == childs[i].name;
			});
			childs[i].allowedValues = a.allowedValues.filter((allowedValue: any) => (parentMask == null || allowedValue == null || allowedValue.parentMask == parentMask));
		}
	}

	getParentField(fieldIndex: number): any | null {
		for (let i = 0; i < this.batchEditFields.length; i++) {
			if (this.batchEditFields[i].allowedValues) {
				for (let j = 0; j < this.batchEditFields[i].allowedValues.length; j++) {
					if (this.batchEditFields[i].allowedValues[j] != null && this.batchEditFields[i].allowedValues[j].childs != null) {
						let parentField = this.batchEditFields[i].allowedValues[j].childs.find((x: any) => {
							return x.name == this.batchEditFields[fieldIndex].name;
						});
						return parentField;
					}
				}
			}
		}
		return null;
	}

	// This complicated message logic is required for UDOCA
	getParentFieldRequiredMessage(fieldIndex: number): string | null {
		let parentFieldObject = this.getParentFieldFromFieldList(fieldIndex);
		if (parentFieldObject != null) {
			let a = this.batchEditFields.find((x: any) => {
				return x.name === parentFieldObject?.name;
			});

			if (a == null || !a.content || a.content.length == 0 || a.content[0] == '') {
				return (parentFieldObject) ? '"' + parentFieldObject?.displayName + '"' : '"' + this.batchEditFields[0]?.name + '"';
			}
		}
		return null;
	}

	getParentFieldFromFieldList(fieldIndex: number): any | null {
		for (let i = 0; i < this.fieldList.length; i++) {
			if (this.fieldList[i].allowedValues) {
				for (let j = 0; j < this.fieldList[i].allowedValues.length; j++) {
					if (this.fieldList[i].allowedValues[j] != null && this.fieldList[i].allowedValues[j].childs != null) {
						let parentField = this.fieldList[i].allowedValues[j].childs.find((x: any) => {
							return x.name === this.batchEditFields[fieldIndex].name;
						});
						if (parentField) {
							return this.fieldList[i];
						}
					}
				}
			}
		}
		return null;
	}

	removeBatchSearchReplaceField(fieldIndex: number): void {
		this.batchSearchReplaceFields.splice(fieldIndex, 1);
	}

	addBatchSearchReplaceField(): void {
		this.batchSearchReplaceFields.push({
			name: this.fieldList[0].name,
			search: "",
			replacement: ""
		});
		this.updateBatchSearchReplaceField(this.batchSearchReplaceFields.length - 1);
	}

	/**
	 * Updates the batch edit field based on the fieldIndex.
	 */
	updateBatchSearchReplaceField(fieldIndex: number): void {
		let selectedField = this.fieldList.find((x: any) => {
			return x.name == this.batchSearchReplaceFields[fieldIndex].name;
		});

		let newField = cloneDeep(selectedField);
		this.batchSearchReplaceFields[fieldIndex] = newField;
		this.batchSearchReplaceFields[fieldIndex].search = "";
		this.batchSearchReplaceFields[fieldIndex].replacement = "";
	}

	/**
	 * Updates the metadata of the selected products.
	 */
	save(): void {
		this.inprogress = true;

		let metadata: { [key: string]: any[] } | undefined = {};
		let replacements: { [key: string]: any[] } | undefined = {};

		let payload: {
			ids: any[];
			replaceAll: boolean;
			replaceFieldValues: boolean;
			replacements: { [key: string]: any[] } | undefined;
			metadata: { [key: string]: any[] } | undefined;
		} = {
			ids: this.productGroup.ids,
			replaceAll: false,
			replaceFieldValues: true,
			replacements: replacements,
			metadata: metadata
		};

		if (this.mode === "update") {
			payload.metadata = {};
			payload.replaceFieldValues = this.replaceMode == 'append' ? false : true;

			// Traverse and get only values that aren't empty
			for (let i = 0; i < this.batchEditFields.length; i++) {
				let field = this.batchEditFields[i];
				for (let k = 0; k < field.content.length; k++) {
					let value = field.content[k];
					if (value && value != "") {

						// Attach calendar input values as date objects
						if (this.datePickerSet[field.name]) {
							let [start] = this.datePickerSet[field.name].selectedDates;
							value = start;
						}

						if (payload.metadata[field.name]) {
							payload.metadata[field.name].push(value);
						} else {
							if (value instanceof Array) {
								payload.metadata[field.name] = value;
							} else {
								payload.metadata[field.name] = [value];
							}
						}
					}
				}
			}
		}
		else if (this.mode === "replace") {
			payload.metadata = undefined;
			payload.replaceFieldValues = true;
			payload.replacements = {};
			for (let i = 0; i < this.batchSearchReplaceFields.length; i++) {
				let field = this.batchSearchReplaceFields[i];
				payload.replacements[field.name] = [{ search: field.search, replacement: field.replacement }];
			}
		}

		console.log("Saving metadata. payload:");
		console.log(payload);

		this.productService.massUpdateMetadata(payload).then(
			(response: any) => {
				this.inprogress = false;
				this.alertsService.success({
					message: this.LOCALIZATION.metadataUpdatedMessage + " " + this.LOCALIZATION.searchUpdatesConfirmationMessage,
					delay: 5000
				});
			},
			(err: any) => {
				console.error("Failed to update product metadata.", err);
				this.inprogress = false;
				this.alertsService.error({
					message: "Failed to update metadata."
				});
			}
		);
	}

	trackByIndex(index: number, formItem: any): number {
		return index;
	}

	/**
	 * Opens a date picker for the field.
	 */
	openDatePicker(fieldName: string, fieldIndex: number, valueIndex: number): void {
		if (!this.datePickerSet[fieldName]) {
			let opts = this.datePickerOptions[fieldName];
			let target = this.elementRef.nativeElement.querySelector(`#daterange_${fieldName}${fieldIndex}${valueIndex}`);
			this.datePickerSet[fieldName] = flatpickr(target, opts);
		}
		if (this.datePickerSet[fieldName].isOpen) {
			this.datePickerSet[fieldName].close();
		} else {
			this.datePickerSet[fieldName].open();
		}
	}

	/**
	 * Opens a geotagger for the field.
	 */
	// Bound in the child template
	onGeotag(fieldIndex: number, valueIndex: number): void {
		const modalRef = this.ngbModal.open(GeoTagComponent, {
			size: 'lg',
			windowClass: 'kv-utility-modal kv-utility-large-modal-window',
			animation: false
		});

		modalRef.componentInstance.productGroup = {
			commit: false,
			records: []
		};

		modalRef.result.then(
			(payload) => {
				if (payload.result?.answer === 'ok') {
					this.batchEditFields[fieldIndex].content[valueIndex] = payload.result.caller.result;
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
